import React from 'react';
import { Link } from 'react-router-dom';
import {
  Video, Building, CheckCircle, ArrowRight, Calendar,
  Clock, Star, Leaf, Shield, Heart
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { SERVICES, FAQ, CONDITIONS_TREATED, IMAGES } from '../data/mock';

const ServicesPage = () => {
  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-[#2F5233] to-[#1e3621] overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-20 right-20 w-72 h-72 bg-white/5 rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 left-10 w-96 h-96 bg-white/5 rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-flex items-center space-x-2 bg-white/10 rounded-full px-4 py-2 mb-6">
            <Leaf className="w-4 h-4 text-green-300" />
            <span className="text-white text-sm font-medium">Our Services</span>
          </span>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Consultation Services
          </h1>
          <p className="text-lg text-white/80 max-w-2xl mx-auto">
            Choose the consultation option that works best for you. 
            Both options provide comprehensive care and personalized treatment.
          </p>
        </div>
      </section>

      {/* Services Cards */}
      <section className="py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            {SERVICES.map((service) => (
              <Card key={service.id} className="group overflow-hidden hover:shadow-2xl transition-all duration-500 border-2 border-transparent hover:border-[#2F5233]/20">
                <CardContent className="p-0">
                  {/* Header */}
                  <div className={`p-6 ${service.icon === 'video' ? 'bg-blue-50' : 'bg-green-50'}`}>
                    <div className="flex items-start justify-between">
                      <div className={`w-16 h-16 rounded-2xl flex items-center justify-center ${service.icon === 'video' ? 'bg-blue-100' : 'bg-green-100'}`}>
                        {service.icon === 'video' ? (
                          <Video className="w-8 h-8 text-blue-600" />
                        ) : (
                          <Building className="w-8 h-8 text-green-600" />
                        )}
                      </div>
                      <div className="text-right">
                        <p className="text-3xl font-bold text-[#2F5233]">₹{service.price.toLocaleString()}</p>
                        <p className="text-sm text-gray-500">{service.duration}</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Content */}
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{service.title}</h3>
                    <p className="text-gray-600 mb-6">{service.description}</p>
                    
                    <ul className="space-y-3 mb-6">
                      {service.features.map((feature, index) => (
                        <li key={index} className="flex items-center space-x-3">
                          <CheckCircle className="w-5 h-5 text-[#2F5233] flex-shrink-0" />
                          <span className="text-gray-700">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    
                    <Link to={`/book?type=${service.icon === 'video' ? 'online' : 'offline'}`}>
                      <Button className="w-full bg-[#2F5233] hover:bg-[#2F5233]/90 text-white group-hover:shadow-lg transition-all duration-300">
                        Book {service.title}
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          
          {/* Note */}
          <div className="mt-8 bg-[#2F5233]/5 rounded-2xl p-6">
            <div className="flex items-start space-x-4">
              <div className="w-10 h-10 rounded-full bg-[#2F5233]/10 flex items-center justify-center flex-shrink-0">
                <Clock className="w-5 h-5 text-[#2F5233]" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">Clinic Hours</h4>
                <p className="text-gray-600">
                  Mon, Wed - Fri: 11:00 AM - 2:00 PM & 4:00 PM - 7:00 PM
                  <br />
                  Sat & Sun: 11:00 AM - 3:00 PM
                  <br />
                  <span className="text-red-500">Closed on Tuesdays</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What to Expect */}
      <section className="py-20 bg-[#FAFAF8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-[#2F5233] font-medium">What to Expect</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-2">
              Your Consultation Journey
            </h2>
          </div>
          
          <div className="grid md:grid-cols-4 gap-8">
            {[
              {
                step: '01',
                title: 'Book Appointment',
                description: 'Select your preferred consultation type, date, and time slot.'
              },
              {
                step: '02',
                title: 'Case Analysis',
                description: 'Dr. Bhatia reviews your symptoms, history, and uploaded reports.'
              },
              {
                step: '03',
                title: 'Consultation',
                description: 'Detailed discussion of your health concerns via video or in-person.'
              },
              {
                step: '04',
                title: 'Treatment Plan',
                description: 'Receive personalized prescription and follow-up guidance.'
              }
            ].map((item, index) => (
              <div key={index} className="relative">
                {index < 3 && (
                  <div className="hidden md:block absolute top-8 left-full w-full h-0.5 bg-[#2F5233]/20 -translate-x-1/2"></div>
                )}
                <Card className="border-0 shadow-md hover:shadow-lg transition-shadow h-full">
                  <CardContent className="p-6">
                    <div className="w-12 h-12 rounded-full bg-[#2F5233] text-white flex items-center justify-center font-bold mb-4">
                      {item.step}
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{item.title}</h3>
                    <p className="text-gray-600 text-sm">{item.description}</p>
                  </CardContent>
                </Card>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Conditions Treated */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <div className="absolute -top-6 -left-6 w-full h-full bg-[#2F5233]/10 rounded-3xl"></div>
              <img
                src={IMAGES.medicine}
                alt="Homoeopathic Medicine"
                className="relative rounded-3xl shadow-2xl w-full h-[450px] object-cover"
              />
            </div>
            
            <div>
              <span className="text-[#2F5233] font-medium">Conditions We Treat</span>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-2 mb-6">
                Comprehensive Care
              </h2>
              <p className="text-gray-600 mb-8">
                Our holistic approach addresses a wide range of acute and chronic conditions. 
                We work with your body's natural healing abilities.
              </p>
              
              <div className="grid grid-cols-2 gap-4">
                {CONDITIONS_TREATED.map((condition, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <div className="w-2 h-2 rounded-full bg-[#2F5233]"></div>
                    <span className="text-gray-700 text-sm">{condition}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Homoeopathy */}
      <section className="py-20 bg-gradient-to-br from-[#2F5233] to-[#1e3621]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white">
              Why Choose Homoeopathy?
            </h2>
            <p className="text-white/80 mt-4">
              Discover the benefits of natural, holistic healing
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: Shield,
                title: 'Safe & Gentle',
                description: 'No side effects, suitable for all ages including infants and pregnant women.'
              },
              {
                icon: Heart,
                title: 'Treats Root Cause',
                description: 'Addresses the underlying cause of illness, not just the symptoms.'
              },
              {
                icon: Star,
                title: 'Long-lasting Results',
                description: 'Promotes permanent healing and strengthens natural immunity.'
              }
            ].map((item, index) => (
              <Card key={index} className="bg-white/10 border-white/10 backdrop-blur-sm">
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 rounded-2xl bg-white/10 flex items-center justify-center mx-auto mb-6">
                    <item.icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-3">{item.title}</h3>
                  <p className="text-white/70">{item.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-[#2F5233] font-medium">FAQ</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-2">
              Frequently Asked Questions
            </h2>
          </div>
          
          <Accordion type="single" collapsible className="space-y-4">
            {FAQ.map((item, index) => (
              <AccordionItem key={index} value={`item-${index}`} className="border rounded-xl px-6">
                <AccordionTrigger className="text-left font-medium text-gray-900 hover:no-underline">
                  {item.question}
                </AccordionTrigger>
                <AccordionContent className="text-gray-600">
                  {item.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#FAFAF8]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Ready to Start Your Healing Journey?
          </h2>
          <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
            Book your consultation today and experience the transformative power of homoeopathy.
          </p>
          <Link to="/book">
            <Button size="lg" className="bg-[#2F5233] hover:bg-[#2F5233]/90 text-white shadow-lg">
              <Calendar className="w-5 h-5 mr-2" />
              Book Your Consultation
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
